import {
  createUploadInputSchema,
  type ActivityDetail,
  type ActivityFavoriteResponse,
  type ActivityNoteMutationResponse,
  type ActivityDocument,
  type ActivityEditorOptions,
  type ActivityMutationResponse,
  type CreateActivityInput,
  type CreateUploadResponse,
  type DuplicateLinkCheckInput,
  type DuplicateLinkCheckResponse,
  type ResourceMetadata,
  type SaveActivityNoteInput,
  type UpdateActivityInput,
  type UploadFile,
  type CompleteSessionInput,
  type SessionCompletionResponse,
  type CompletionReflectionResponse,
  type CreateActivitySessionInput,
  type PlanningOptionsResponse,
  type SaveCompletionReflectionInput,
  type SessionMutationInput,
  type SessionMutationResponse,
  type UpdateActivitySessionInput,
} from "@learn-flow/contracts";

import { ApiError, apiRequest } from "../../lib/api.js";

export const activityEditorOptionsKey = ["activity-editor", "options"] as const;
export const activityEditorKey = (activityId: string) => ["activity-editor", activityId] as const;
export const activityDetailKey = (activityId: string) => ["activity-detail", activityId] as const;

export function loadActivityEditorOptions(): Promise<ActivityEditorOptions> {
  return apiRequest("/api/v1/activity-editor/options");
}

export function loadActivityEditor(activityId: string): Promise<ActivityDocument> {
  return apiRequest(`/api/v1/activities/${activityId}/editor`);
}

export function loadActivityDetail(activityId: string): Promise<ActivityDetail> {
  return apiRequest(`/api/v1/activities/${activityId}`);
}

export function saveActivityNote(
  activityId: string,
  input: SaveActivityNoteInput,
): Promise<ActivityNoteMutationResponse> {
  return apiRequest(`/api/v1/activities/${activityId}/note`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function setActivityFavorite(
  activityId: string,
  favorite: boolean,
): Promise<ActivityFavoriteResponse> {
  return apiRequest(`/api/v1/activities/${activityId}/favorite`, {
    method: "PUT",
    body: JSON.stringify({ favorite }),
  });
}

export function createActivitySession(
  activityId: string,
  input: CreateActivitySessionInput,
): Promise<SessionMutationResponse> {
  return apiRequest(`/api/v1/activities/${activityId}/sessions`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateActivitySession(
  sessionId: string,
  input: UpdateActivitySessionInput,
): Promise<SessionMutationResponse> {
  return apiRequest(`/api/v1/sessions/${sessionId}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function cancelActivitySession(
  sessionId: string,
  input: SessionMutationInput,
): Promise<SessionMutationResponse> {
  return apiRequest(`/api/v1/sessions/${sessionId}/cancel`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function completeActivitySession(
  sessionId: string,
  input: CompleteSessionInput,
): Promise<SessionCompletionResponse> {
  return apiRequest(`/api/v1/sessions/${sessionId}/complete`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function loadSessionPlanningOptions(
  activityId: string,
  completionId: string,
): Promise<PlanningOptionsResponse> {
  const query = new URLSearchParams({ completionId });
  return apiRequest(
    `/api/v1/activities/${activityId}/session-planning-options?${query.toString()}`,
  );
}

export function undoActivitySession(
  sessionId: string,
  input: SessionMutationInput,
): Promise<SessionMutationResponse> {
  return apiRequest(`/api/v1/sessions/${sessionId}/undo-completion`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function saveCompletionReflection(
  activityId: string,
  completionId: string,
  input: SaveCompletionReflectionInput,
): Promise<CompletionReflectionResponse> {
  return apiRequest(
    `/api/v1/activities/${activityId}/completions/${completionId}/reflection-plan`,
    {
      method: "PUT",
      body: JSON.stringify(input),
    },
  );
}

export function checkUploadAvailability(fileId: string): Promise<{ available: true }> {
  return apiRequest(`/api/v1/uploads/${fileId}/availability`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function checkExternalMaterialAvailability(
  activityId: string,
  materialId: string,
): Promise<{ availability: "available" }> {
  return apiRequest(`/api/v1/activities/${activityId}/materials/${materialId}/availability`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function createActivity(input: CreateActivityInput): Promise<ActivityMutationResponse> {
  return apiRequest("/api/v1/activities", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateActivity(
  activityId: string,
  input: UpdateActivityInput,
): Promise<ActivityMutationResponse> {
  return apiRequest(`/api/v1/activities/${activityId}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export function checkDuplicateActivityLinks(
  input: DuplicateLinkCheckInput,
): Promise<DuplicateLinkCheckResponse> {
  return apiRequest("/api/v1/activity-link-duplicates", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function fetchResourceMetadata(url: string): Promise<ResourceMetadata> {
  return apiRequest("/api/v1/resource-metadata", {
    method: "POST",
    body: JSON.stringify({ url }),
  });
}

export async function uploadFile(
  file: File,
  kind: "image" | "file",
  onProgress: (percentage: number) => void,
  onPending: (file: UploadFile) => boolean,
): Promise<UploadFile> {
  const checksum = await sha256(file);
  const input = createUploadInputSchema.parse({
    kind,
    originalFilename: file.name,
    contentType: file.type,
    size: file.size,
    checksum,
  });
  const pending = await apiRequest<CreateUploadResponse>("/api/v1/uploads", {
    method: "POST",
    body: JSON.stringify(input),
  });
  if (!onPending(pending.file)) {
    await removeUpload(pending.file.id).catch(() => undefined);
    throw new Error("The upload was cancelled.");
  }
  try {
    await transferFile(file, pending, onProgress);
    const confirmed = await apiRequest<{ file: UploadFile }>(
      `/api/v1/uploads/${pending.file.id}/confirm`,
      { method: "POST", body: JSON.stringify({}) },
    );
    return confirmed.file;
  } catch (error) {
    await removeUpload(pending.file.id).catch(() => undefined);
    throw error;
  }
}

export function removeUpload(fileId: string): Promise<void> {
  return apiRequest(`/api/v1/uploads/${fileId}`, {
    method: "DELETE",
    body: JSON.stringify({}),
  });
}

async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function transferFile(
  file: File,
  pending: CreateUploadResponse,
  onProgress: (percentage: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(pending.upload.method, pending.upload.url);
    request.withCredentials = pending.upload.url.startsWith("/");
    for (const [name, value] of Object.entries(pending.upload.headers)) {
      request.setRequestHeader(name, value);
    }
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
      }
    });
    request.addEventListener("load", () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress(100);
        resolve();
      } else {
        reject(new ApiError(request.status, null));
      }
    });
    request.addEventListener("error", () => reject(new Error("The upload was interrupted.")));
    request.addEventListener("abort", () => reject(new Error("The upload was cancelled.")));
    request.send(file);
  });
}
